/**
 * AdminPlatformSummaryCards Component
 *
 * Displays platform-wide token usage metrics for the admin dashboard.
 * Shows: Total Tokens, Total Cost, API Calls, Active Users.
 */

import React from 'react';
import { Coins, DollarSign, Activity, Users, AlertCircle } from 'lucide-react';
import type { AdminPlatformUsageSummary } from '../../types/tokenUsage';

export interface AdminPlatformSummaryCardsProps {
  summary: AdminPlatformUsageSummary | null;
  loading?: boolean;
  error?: string | null;
}

const formatNumber = (n: number): string => n.toLocaleString('en-US');

const formatCurrency = (n: number): string => {
  const decimalPlaces = n < 1 && n > 0 ? 4 : 2;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
};

const CardSkeleton: React.FC = () => (
  <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 animate-pulse">
    <div className="flex items-center">
      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      <div className="ml-4 flex-1">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16" />
      </div>
    </div>
  </div>
);

const AdminPlatformSummaryCards: React.FC<AdminPlatformSummaryCardsProps> = ({
  summary,
  loading = false,
  error = null,
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" role="status" aria-busy="true">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 flex items-center">
        <AlertCircle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" />
        <span className="text-red-700 dark:text-red-400">{error}</span>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
        <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400">No usage data available</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-platform-summary-cards" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Tokens */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-blue-500">
        <div className="flex items-center">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Coins className="w-6 h-6 text-primary-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tokens</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">
              {formatNumber(summary.total_tokens)}
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
          <div className="flex justify-between">
            <span>Prompt:</span>
            <span>{formatNumber(summary.prompt_tokens)}</span>
          </div>
          <div className="flex justify-between">
            <span>Completion:</span>
            <span>{formatNumber(summary.completion_tokens)}</span>
          </div>
        </div>
      </div>

      {/* Total Cost */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-green-500">
        <div className="flex items-center">
          <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <DollarSign className="w-6 h-6 text-green-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">
              {formatCurrency(summary.total_cost)}
            </p>
          </div>
        </div>
      </div>

      {/* API Calls */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-purple-500">
        <div className="flex items-center">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Activity className="w-6 h-6 text-purple-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">API Calls</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">
              {formatNumber(summary.api_call_count)}
            </p>
          </div>
        </div>
      </div>

      {/* Active Users */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-orange-500">
        <div className="flex items-center">
          <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
            <Users className="w-6 h-6 text-orange-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">
              {formatNumber(summary.active_user_count)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPlatformSummaryCards;
